import { SimpleTableConfig } from "@/components/shared/UniversalCRMTable/types";
import type { Communications } from "@/src/gql/graphql";
import {
  coreCommunicationsFields,
  contactCommunicationsFields,
  statusCommunicationsFields,
  metadataCommunicationsFields,
  communicationsActions,
  communicationsDetailActions,
  communicationsFeatures,
  communicationsSavedViews,
  communicationsCustomFilterHooks,
} from "../config";

/**
 * Unified Communications Configuration
 * This configuration serves both table display and form creation/editing for communications
 * Communications are tracked via the communications table
 *
 * Configuration is modularized into separate files for maintainability:
 * - communications-fields-core.ts: title, body, type_id, direction
 * - communications-fields-contact.ts: contact_id, company_id
 * - communications-fields-metadata.ts: status, occurred_at, created_at
 * - communications-features.ts: feature flags
 * - communications-actions.ts: CRUD actions
 * - communications-saved-views.ts: predefined views
 */
export const COMMUNICATIONS_CONFIG: SimpleTableConfig<Communications> = {
  entity: "communications",
  displayName: "Communication",
  statusFieldId: "status",
  defaultPageSize: 10,
  defaultSort: { field: "occurred_at", direction: "desc" },
  autoIncludeUserId: true,
  rowActionsPlacement: "inlineTitle",

  /** Default table column order: type → direction → who → account → when → subject → outcome */
  tableColumnOrder: [
    "type_label",
    "direction",
    "contact_name",
    "company_name",
    "occurred_at",
    "title",
    "status",
  ],

  /** Card view: subject → outcome → type → when → who */
  cardFaceFieldOrder: [
    "title",
    "status",
    "type_label",
    "occurred_at",
    "contact_name",
    "company_name",
  ],

  mobileDetailSections: [
    { id: "details", label: "Details" },
    { id: "contact", label: "Contact & Company" },
    { id: "status", label: "Status" },
    { id: "timing", label: "Timing" },
    { id: "system", label: "System" },
  ],
  mobileDetailUnsectionedLabel: "Other",

  fields: {
    ...coreCommunicationsFields,
    ...contactCommunicationsFields,
    ...statusCommunicationsFields,
    ...metadataCommunicationsFields,
  },

  actions: communicationsActions,

  features: communicationsFeatures,

  savedViews: communicationsSavedViews,

  detailActions: communicationsDetailActions,

  // Custom filter hooks for saved views (can be populated later if needed)
  customFilterHooks: communicationsCustomFilterHooks,
};
